import React, { useEffect, useRef, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
} from '@hello-pangea/dnd';
import { BaseButton } from '@repo/common';

const MAX_IMAGE_COUNT = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const WorkshopImageUploader = ({
  images,
  onChange,
  error,
  disabled = false,
}) => {
  const [fileError, setFileError] = useState('');
  const imagesRef = useRef(images);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(
    () => () => {
      imagesRef.current.forEach((image) => {
        if (image.file) URL.revokeObjectURL(image.previewUrl);
      });
    },
    [],
  );

  const handleFiles = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';
    setFileError('');

    if (images.length + selectedFiles.length > MAX_IMAGE_COUNT) {
      setFileError(`You can upload up to ${MAX_IMAGE_COUNT} images.`);
      return;
    }

    const invalidFile = selectedFiles.find(
      (file) => !file.type.startsWith('image/') || file.size > MAX_FILE_SIZE,
    );
    if (invalidFile) {
      setFileError('Use image files up to 10 MB each.');
      return;
    }

    const nextImages = selectedFiles.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    onChange([...images, ...nextImages]);
  };

  const removeImage = (id) => {
    const target = images.find((image) => image.id === id);
    if (target?.file) URL.revokeObjectURL(target.previewUrl);
    onChange(images.filter((image) => image.id !== id));
  };

  const moveImage = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const nextImages = [...images];
    const [movedImage] = nextImages.splice(fromIndex, 1);
    nextImages.splice(toIndex, 0, movedImage);
    onChange(nextImages);
  };

  const handleDragEnd = ({ source, destination }) => {
    if (!destination || source.index === destination.index) return;
    moveImage(source.index, destination.index);
  };

  return (
    <div>
      <div className='mb-3 flex items-center justify-between'>
        <div>
          <p className='typo-b14Strong text-text-primary'>Workshop images *</p>
          <p className='mt-1 typo-b12 text-text-secondary'>
            Drag images to reorder them. The first image becomes the cover.
          </p>
        </div>
        <label className='cursor-pointer rounded-lg border border-green-deep02 px-4 py-2 typo-b14Strong text-green-deep02 hover:bg-green-light01'>
          + Add images
          <input
            type='file'
            accept='image/*'
            multiple
            disabled={disabled}
            onChange={handleFiles}
            className='sr-only'
          />
        </label>
      </div>

      {images.length === 0 ? (
        <label className='flex min-h-[180px] cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-grayscale-gray03 bg-grayscale-gray01 px-6 text-center hover:border-green-deep02'>
          <span className='typo-b16Strong text-text-primary'>
            Choose workshop images
          </span>
          <span className='mt-2 typo-b12 text-text-secondary'>
            JPG, PNG, GIF, or WebP · up to 10 MB each
          </span>
          <input
            type='file'
            accept='image/*'
            multiple
            disabled={disabled}
            onChange={handleFiles}
            className='sr-only'
          />
        </label>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='workshop-images' direction='horizontal'>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className='flex gap-4 overflow-x-auto pb-3'
              >
                {images.map((image, index) => (
                  <Draggable
                    key={image.id}
                    draggableId={image.id}
                    index={index}
                    isDragDisabled={disabled}
                  >
                    {(dragProvided, snapshot) => (
                      <article
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className={`w-[250px] shrink-0 overflow-hidden rounded-xl border bg-white ${
                          snapshot.isDragging
                            ? 'border-green-deep02 shadow-lg'
                            : 'border-grayscale-gray02'
                        }`}
                      >
                        <div className='relative aspect-[4/3] bg-grayscale-gray02'>
                          <img
                            src={image.previewUrl}
                            alt={`Workshop preview ${index + 1}`}
                            className='size-full object-cover'
                          />
                          <span className='absolute left-2 top-2 rounded-full bg-grayscale-gray09 px-2 py-1 typo-b12Strong text-white'>
                            {index === 0 ? 'Cover' : index + 1}
                          </span>
                        </div>
                        <div className='flex items-center justify-between gap-1 p-2'>
                          <BaseButton
                            type='text'
                            size='s'
                            htmlType='button'
                            hideIcon
                            typo={false}
                            onClick={() => moveImage(index, index - 1)}
                            isDisabled={disabled || index === 0}
                            disabledTextClass='text-text-secondary'
                            aria-label={`Move image ${index + 1} left`}
                            className='h-auto min-w-0 rounded border-0 bg-transparent px-2 py-1 text-text-secondary hover:bg-grayscale-gray01 hover:text-text-secondary disabled:opacity-25'
                            textWrapperClass='h-auto'
                          >
                            ←
                          </BaseButton>
                          <span
                            title={image.file?.name || 'Existing image'}
                            className='min-w-0 flex-1 truncate text-center typo-b12 text-text-secondary'
                          >
                            Drag to reorder
                          </span>
                          <BaseButton
                            type='text'
                            size='s'
                            htmlType='button'
                            hideIcon
                            typo={false}
                            onClick={() => moveImage(index, index + 1)}
                            isDisabled={disabled || index === images.length - 1}
                            disabledTextClass='text-text-secondary'
                            aria-label={`Move image ${index + 1} right`}
                            className='h-auto min-w-0 rounded border-0 bg-transparent px-2 py-1 text-text-secondary hover:bg-grayscale-gray01 hover:text-text-secondary disabled:opacity-25'
                            textWrapperClass='h-auto'
                          >
                            →
                          </BaseButton>
                        </div>
                        <BaseButton
                          type='text'
                          size='s'
                          htmlType='button'
                          hideIcon
                          typo={false}
                          onClick={() => removeImage(image.id)}
                          isDisabled={disabled}
                          disabledTextClass='text-red-600'
                          className='h-auto w-full min-w-0 rounded-none border-0 border-t border-grayscale-gray02 bg-transparent px-3 py-2 typo-b12Strong text-red-600 hover:bg-red-50 hover:text-red-600 disabled:opacity-50'
                          textWrapperClass='h-auto'
                        >
                          Remove
                        </BaseButton>
                      </article>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {(fileError || error) && (
        <p className='mt-2 typo-b12 text-red-600'>{fileError || error}</p>
      )}
    </div>
  );
};

export default WorkshopImageUploader;
